// ============================================================
// APP.TSX — completo e aggiornato
// ============================================================

import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

// Providers
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { NotificationProvider } from './contexts/NotificationContext';

// Guards
import PrivateRoute from './components/common/PrivateRoute';
import RoleGuard from './components/common/RoleGuard';
import AuthGuard from './components/common/AuthGuard';
import ScrollToTop from './components/common/ScrollToTop';

// Layout pubblico
import Layout from './components/layout/Layout';
import InstallBanner from './components/common/installBanner/InstallBanner';

// Layout dashboard utente
import UserLayout from './components/layout/UserLayout/UserLayout';

// Pagine pubbliche
import Home from './components/pages/home/Home';
import Servizi from './components/pages/servizi/Servizi';
import RimettitiAlCentro from './components/pages/rimettiti-al-centro/RimettitiAlCentro';
import InRegalo from './components/pages/inRegalo/InRegalo';
import ChiSono from './components/pages/chi-sono/ChiSono';
import Checkout from './components/pages/checkout/Checkout';
import OrdineOk from './components/pages/ordine-ok/OrdineOk';
import ResetPassword from './components/pages/reset-password/ResetPassword';
import ForcePasswordChange from './components/pages/cambio-password-obbligatorio/ForcePasswordChange';
import SocialCallback from './components/pages/SocialCallback';

// ── Pagine area riservata ────────────────────────
// USER DASHBOARD
import DashboardHome from './components/pages/dashboard/DashboardHome';
import MyCourses from './components/pages/dashboard/MyCourses/MyCourses';
import CoursePlayer from './components/pages/dashboard/CoursePlayer/CoursePlayer';
import FitAnatomy from './components/pages/dashboard/FitAnatomy/FitAnatomy';
import UserProfile from './components/pages/dashboard/UserProfile/UserProfile';
import Notifications from './components/pages/dashboard/Notifications/Notifications';

// ADMIN DASHBOARD
import AdminLayout from './components/layout/AdminLayout/AdminLayout';
import AdminOverview from './components/pages/admin/AdminOverview/AdminOverview';
import AdminUsers from './components/pages/admin/AdminUsers/AdminUsers';
import AdminUserDetail from './components/pages/admin/AdminUserDetail/AdminUserDetail';
import AdminOrders from './components/pages/admin/AdminOrders/AdminOrders';
import AdminCourses from './components/pages/admin/AdminCourses/AdminCourses';
import AdminCommunications from './components/pages/admin/AdminCommunications/AdminCommunications';

// SUPERADMIN DASHBOARD — riusa le pagine admin, vedi SuperAdminLayout
import SuperAdminLayout from './components/layout/SuperAdminLayout/SuperAdminLayout';

import './App.css';

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <ScrollToTop />
            <InstallBanner />
            <Routes>
              {/* Callback OAuth — popup aperto dal backend, nessun layout */}
              <Route path="/auth/social/success" element={<SocialCallback />} />

              {/* ═══════════════════════════════════════════════
                  ROTTE PUBBLICHE — con Header/Footer
              ════════════════════════════════════════════════ */}
              <Route path="/" element={<Layout />}>
                <Route index element={<Home />} />
                <Route path="servizi" element={<Servizi />} />
                <Route
                  path="rimettiti-al-centro"
                  element={<RimettitiAlCentro />}
                />
                <Route path="in-regalo" element={<InRegalo />} />
                <Route path="chi-sono" element={<ChiSono />} />
                <Route path="/reset-password" element={<ResetPassword />} />
              </Route>

              {/* ═══════════════════════════════════════════════
                  CHECKOUT — senza Layout, protetto da PrivateRoute
              ════════════════════════════════════════════════ */}
              <Route
                path="/checkout"
                element={
                  <PrivateRoute>
                    <Checkout />
                  </PrivateRoute>
                }
              />
              <Route
                path="/ordine-ok"
                element={
                  <PrivateRoute>
                    <OrdineOk />
                  </PrivateRoute>
                }
              />

              {/* Cambio password obbligatorio — solo admin/superadmin con
                  password temporanea. AuthGuard reindirizza qui automaticamente. */}
              <Route
                path="/cambio-password-obbligatorio"
                element={
                  <PrivateRoute>
                    <ForcePasswordChange />
                  </PrivateRoute>
                }
              />

              {/* ═══════════════════════════════════════════════
                  DASHBOARD UTENTE — /dashboard/*
                  Richiede: autenticazione, ruolo 'user'
                  strictRole: admin/superadmin vengono rimandati
                  alle proprie aree
              ════════════════════════════════════════════════ */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <RoleGuard requiredRole="user" strictRole>
                      <NotificationProvider>
                        <AuthGuard>
                          <UserLayout />
                        </AuthGuard>
                      </NotificationProvider>
                    </RoleGuard>
                  </PrivateRoute>
                }
              >
                <Route index element={<DashboardHome />} />
                <Route path="corsi" element={<MyCourses />} />
                <Route path="corsi/:courseId" element={<CoursePlayer />} />
                <Route path="fit-anatomy" element={<FitAnatomy />} />
                <Route path="profilo" element={<UserProfile />} />
                <Route path="notifiche" element={<Notifications />} />
              </Route>

              {/* ═══════════════════════════════════════════════
                  DASHBOARD ADMIN — /admin/*
                  Richiede: ruolo 'admin' o superiore
                  strictRole: superadmin viene rimandato a /superadmin
              ════════════════════════════════════════════════ */}
              <Route
                path="/admin"
                element={
                  <PrivateRoute>
                    <RoleGuard
                      requiredRole="admin"
                      strictRole
                      redirectTo="/dashboard"
                    >
                      <AuthGuard>
                        <AdminLayout />
                      </AuthGuard>
                    </RoleGuard>
                  </PrivateRoute>
                }
              >
                <Route index element={<AdminOverview />} />
                <Route path="utenti" element={<AdminUsers />} />
                <Route path="utenti/:id" element={<AdminUserDetail />} />
                <Route path="corsi" element={<AdminCourses />} />
                <Route path="ordini" element={<AdminOrders />} />
                <Route path="comunicazioni" element={<AdminCommunications />} />
              </Route>

              {/* ═══════════════════════════════════════════════
                  DASHBOARD SUPERADMIN — /superadmin/*
                  Richiede: ruolo 'superadmin'. Riusa le stesse pagine
                  dell'area admin (controllo totale = superset di admin);
                  la gestione dell'account admin stesso avviene da qui
                  tramite AdminUsers/AdminUserDetail, protetta dalla
                  gerarchia dei ruoli (RoleGuard::canManageUser).
              ════════════════════════════════════════════════ */}
              <Route
                path="/superadmin"
                element={
                  <PrivateRoute>
                    <RoleGuard requiredRole="superadmin" redirectTo="/admin">
                      <AuthGuard>
                        <SuperAdminLayout />
                      </AuthGuard>
                    </RoleGuard>
                  </PrivateRoute>
                }
              >
                <Route index element={<AdminOverview />} />
                <Route path="utenti" element={<AdminUsers />} />
                <Route path="utenti/:id" element={<AdminUserDetail />} />
                <Route path="corsi" element={<AdminCourses />} />
                <Route path="ordini" element={<AdminOrders />} />
                <Route path="comunicazioni" element={<AdminCommunications />} />
              </Route>

              {/* ═══════════════════════════════════════════════
                  CATCH-ALL
              ════════════════════════════════════════════════ */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
